import React, { PropsWithChildren, createContext, useEffect, useRef, useState } from 'react';
import { APPLICATION_URL, RoleType, AuthEndpoint, EMAIL_VERIFICATION_ERROR } from '../../common/constants';
import {
    extractOrgFromEntityAssociations,
    extractTeamIdsFromEntityAssociations,
    extractUserRequestsFromCurrentUser,
    useGetCurrentUser,
    UserRequestData,
} from '../../hooks/user/getCurrentUser';
import { getNodeEnvironment } from '../../common/nodeEnvironment';

export interface ApplicationUserInitProps {
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    isLoggedIn?: boolean;
    role?: RoleType;
    organization?: string;
    organizationId?: string;
    organizationType?: string;
    teamIds?: string[];
    userRequests?: UserRequestData[];
}

export interface AuthResponse {
    user: ApplicationUser;
    isAuthError?: boolean;
    authErrorMessage?: string;
    loading: boolean;
    userRequests?: UserRequestData[];
    refetchUser: () => void;
}

export class ApplicationUser {
    readonly id?: string;
    readonly firstName?: string;
    readonly lastName?: string;
    readonly email?: string;
    readonly isLoggedIn?: boolean;
    readonly role?: RoleType;
    readonly organization?: string;
    readonly organizationId?: string;
    readonly organizationType?: string;
    readonly teamIds?: string[];
    readonly userRequests?: UserRequestData[];

    constructor(initProps: ApplicationUserInitProps) {
        this.id = initProps.id;
        this.firstName = initProps.firstName;
        this.lastName = initProps.lastName;
        this.email = initProps.email;
        this.isLoggedIn = initProps.isLoggedIn;
        this.role = initProps.role;
        this.organization = initProps.organization;
        this.organizationId = initProps.organizationId;
        this.organizationType = initProps.organizationType;
        this.teamIds = initProps.teamIds;
        this.userRequests = initProps.userRequests;
    }
}

interface AuthContextProps extends AuthResponse {}

export const AuthContext = createContext<AuthContextProps>({
    user: new ApplicationUser({}),
    loading: true,
    isAuthError: false,
    authErrorMessage: undefined,
    refetchUser: () => {},
});

const addParamsAndCreateFullURL = (endpoint: AuthEndpoint, searchParams: Record<string, string> = {}) => {
    const nodeEnvironment = getNodeEnvironment();
    let currentDomain;
    switch (nodeEnvironment) {
        case 'beta':
            currentDomain = APPLICATION_URL.BETA;
            break;
        case 'gamma':
            currentDomain = APPLICATION_URL.GAMMA;
            break;
        case 'prod':
            currentDomain = APPLICATION_URL.PROD;
            break;
        default:
            currentDomain = 'http://localhost:3000';
            break;
    }
    const urlWithPath = new URL(`${currentDomain}/${endpoint}`);
    Object.entries(searchParams).forEach(([key, value]) => {
        urlWithPath.searchParams.set(key, value);
    });
    urlWithPath.searchParams.set('redirect_uri', searchParams.redirect_uri ?? currentDomain);

    return urlWithPath.toString();
};

const getSignInURL = (searchParams: Record<string, string> = {}) => {
    return addParamsAndCreateFullURL(AuthEndpoint.SIGN_IN, searchParams);
};

const refreshToken = async (): Promise<Response> => {
    const refreshTokenURL = addParamsAndCreateFullURL(AuthEndpoint.REFRESH_TOKEN);
    return fetch(refreshTokenURL, { method: 'POST' });
};

const isAuthorizationError = (error: any): boolean => {
    return (
        error?.cause?.result?.Message?.toLowerCase().includes('not authorized') ||
        error?.cause?.result?.Message?.toLowerCase().includes('unauthorized')
    );
};

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [id, setId] = useState<string | undefined>(undefined);
    const [firstName, setFirstName] = useState<string | undefined>(undefined);
    const [lastName, setLastName] = useState<string | undefined>(undefined);
    const [signedInUserEmail, setSignedInUserEmail] = useState<string | undefined>(undefined);
    const [isLoggedIn, setIsLoggedIn] = useState<boolean | undefined>(undefined);
    const [userRole, setUserRole] = useState<RoleType | undefined>(undefined);
    const [authError, setAuthError] = useState<string | undefined>(undefined);
    const [organizationId, setOrganizationId] = useState<string | undefined>(undefined);
    const [teamIds, setTeamIds] = useState<string[] | undefined>(undefined);
    const [organization, setOrganization] = useState<string | undefined>(undefined);
    const [organizationType, setOrganizationType] = useState<string | undefined>(undefined);
    const [userRequests, setUserRequests] = useState<UserRequestData[] | undefined>(undefined);

    // Track if we've already initiated the auth flow to prevent loops
    const authFlowInitiatedRef = useRef(false);

    const { data, error, loading, refetch } = useGetCurrentUser();

    const setupUserData = (userData: any) => {
        setId(userData.currentUser.id);
        setFirstName(userData.currentUser.firstName);
        setLastName(userData.currentUser.lastName);
        setSignedInUserEmail(userData.currentUser.emailAddress);
        setIsLoggedIn(true);
        setUserRole(userData.currentUser.userRole);
        const {
            name: orgName,
            organizationType: orgType,
            organizationId: orgId,
        } = extractOrgFromEntityAssociations(userData.currentUser?.entityAssociations?.nodes);
        setOrganizationId(orgId);
        setOrganization(orgName);
        setOrganizationType(orgType);
        const { teamIds } = extractTeamIdsFromEntityAssociations(userData.currentUser?.entityAssociations?.nodes);
        setTeamIds(teamIds);
        const extractedUserRequests = extractUserRequestsFromCurrentUser(userData.currentUser?.userRequests?.nodes);
        setUserRequests(extractedUserRequests);
    };

    useEffect(() => {
        // Only process when we have data or error, not during loading
        if (loading) {
            return;
        }

        // Skip if we've already initiated the auth flow
        if (authFlowInitiatedRef.current) {
            return;
        }

        // Mark that we've initiated the auth flow
        authFlowInitiatedRef.current = true;

        // Handle error from getCurrentUser
        if (error && !data) {
            setIsLoggedIn(false);

            // If the user does not have an existing account, UserService will throw an error unless the email is verified.
            if (error.message.includes(EMAIL_VERIFICATION_ERROR)) {
                window.location.href = addParamsAndCreateFullURL(AuthEndpoint.SIGN_OUT, {
                    redirect_uri: getSignInURL({ require_email_verification: 'true' }),
                });
                return;
            }

            // If it's an authorization error, try to refresh token first
            if (isAuthorizationError(error)) {
                refreshToken()
                    .then((response) => {
                        if (response.ok) {
                            // Token refreshed successfully, retry getCurrentUser
                            refetch().then((result) => {
                                if (result.data) {
                                    setupUserData(result.data);
                                } else {
                                    // Still failed after refresh, redirect to sign-out to clear cookies first
                                    window.location.href = addParamsAndCreateFullURL(AuthEndpoint.SIGN_OUT, {
                                        redirect_uri: getSignInURL({ redirect_uri: window.location.href }),
                                    });
                                }
                            });
                        } else {
                            // Refresh token failed (e.g., 400 when user is invalidated).
                            // Redirect to sign-out first to clear invalid cookies, then to sign-in.
                            // This prevents an infinite loop where signIn redirects back due to stale cookies.
                            window.location.href = addParamsAndCreateFullURL(AuthEndpoint.SIGN_OUT, {
                                redirect_uri: getSignInURL({ redirect_uri: window.location.href }),
                            });
                        }
                    })
                    .catch(() => {
                        // Refresh token request failed, redirect to sign-out to clear cookies first
                        window.location.href = addParamsAndCreateFullURL(AuthEndpoint.SIGN_OUT, {
                            redirect_uri: getSignInURL({ redirect_uri: window.location.href }),
                        });
                    });
            } else {
                window.location.href = addParamsAndCreateFullURL(AuthEndpoint.SIGN_OUT, {
                    redirect_uri: getSignInURL({ redirect_uri: window.location.href }),
                });
            }
            return;
        }

        // Handle success from getCurrentUser - just set up the user data
        if (data) {
            setupUserData(data);
        }
    }, [data, error, loading, refetch]);

    const authData: AuthContextProps = {
        user: new ApplicationUser({
            id,
            firstName,
            lastName,
            email: signedInUserEmail,
            isLoggedIn: isLoggedIn,
            role: userRole,
            organizationId: organizationId,
            organization: organization,
            organizationType: organizationType,
            teamIds,
            userRequests,
        }),
        authErrorMessage: authError,
        isAuthError: !!authError,
        loading,
        userRequests,
        refetchUser: () => {
            refetch().then((result) => {
                if (result.data) {
                    setupUserData(result.data);
                }
            });
        },
    };

    return <AuthContext.Provider value={authData}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
    const context = React.useContext(AuthContext);
    if (!context) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
};
